import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Card, Input } from '@clari/ui'
import { AuthLayout } from '@/components/AuthLayout'
import { ErrorBanner } from '@/components/ErrorBanner'
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter'
import { apiErrorMessage } from '@/lib/api-error'
import { authApi } from '@/lib/auth-api'

export function ResetPassword() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    if (password !== confirm) {
      setError(t('validation.passwords_dont_match', { ns: 'errors' }))
      return
    }
    if (!token) {
      setError(apiErrorMessage(null))
      return
    }
    setLoading(true)
    try {
      await authApi.resetPassword(token, password)
      setDone(true)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card>
        {done ? (
          <>
            <h1 className="text-2xl font-semibold">{t('reset_password.success_title')}</h1>
            <p className="mt-1 text-text-muted">{t('reset_password.success_body')}</p>
            <Button className="mt-6" onClick={() => navigate('/login', { replace: true })}>
              {t('reset_password.signin_link')}
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">{t('reset_password.title')}</h1>
            <p className="mt-1 text-text-muted">{t('reset_password.subtitle')}</p>
            <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit}>
              <div>
                <Input
                  label={t('reset_password.password_label')}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  hint={t('reset_password.password_hint')}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <PasswordStrengthMeter password={password} />
              </div>
              <Input
                label={t('reset_password.confirm_label')}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
              {error ? <ErrorBanner message={error} /> : null}
              <Button type="submit" disabled={loading}>
                {t('reset_password.submit')}
              </Button>
            </form>
            <Link to="/login" className="mt-4 inline-block text-sm text-text-muted hover:text-text">
              {t('forgot_password.back_to_signin')}
            </Link>
          </>
        )}
      </Card>
    </AuthLayout>
  )
}
